import React, { useState, useEffect } from 'react';
import { FileText, Plus, Upload, Download, Trash2, Eye, FolderOpen } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { ModuleInfo } from './ui/module-info';
import { usePersistedState } from '../hooks/usePersistedState';
import axios from 'axios';

// URL centralizado que funciona em produção mesmo com .env errado
import { getBackendUrl } from '../utils/config';
const API_URL = getBackendUrl();

const TarefasTab = () => {
  const [tarefas, setTarefas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [categoriaFiltro, setCategoriaFiltro] = usePersistedState('tarefas_categoria', '');
  const [uploading, setUploading] = useState(false);
  
  const [formData, setFormData] = useState({
    titulo: '',
    descricao: '',
    categoria: '',
    file: null
  });

  const categorias = [
    'Procedimentos',
    'Formação',
    'Normas',
    'Comunicados',
    'Outros'
  ];

  useEffect(() => {
    checkUserRole();
    loadTarefas();
  }, [categoriaFiltro]);

  const checkUserRole = () => {
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    setIsAdmin(user.role === 'admin');
  };

  const loadTarefas = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const params = categoriaFiltro ? { categoria: categoriaFiltro } : {};
      
      const response = await axios.get(`${API_URL}/api/tarefas`, {
        params,
        headers: { Authorization: `Bearer ${token}` }
      });
      setTarefas(response.data);
      setError(null);
    } catch (err) {
      setError('Erro ao carregar tarefas');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.file) {
      setError('Por favor, selecione um ficheiro PDF');
      return;
    }
    
    try {
      setUploading(true);
      const token = localStorage.getItem('token');
      
      const uploadFormData = new FormData();
      uploadFormData.append('file', formData.file);
      
      // Construir query params
      const params = new URLSearchParams({
        titulo: formData.titulo,
        descricao: formData.descricao || '',
        categoria: formData.categoria || 'Outros'
      });
      
      await axios.post(
        `${API_URL}/api/tarefas/upload?${params.toString()}`,
        uploadFormData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          }
        }
      );
      
      setShowModal(false);
      resetForm();
      loadTarefas();
    } catch (err) {
      const errorMsg = err.response?.data?.detail;
      setError(
        typeof errorMsg === 'string' 
          ? errorMsg 
          : JSON.stringify(errorMsg) || 'Erro ao fazer upload'
      );
    } finally {
      setUploading(false);
    }
  };

  const handleDownload = async (tarefaId, fileName) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${API_URL}/api/tarefas/${tarefaId}/download`,
        {
          headers: { Authorization: `Bearer ${token}` },
          responseType: 'blob'
        }
      );
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', fileName);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (err) {
      setError('Erro ao fazer download');
    }
  };

  const handleView = async (tarefaId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${API_URL}/api/tarefas/${tarefaId}/download`,
        {
          headers: { Authorization: `Bearer ${token}` },
          responseType: 'blob'
        }
      );
      
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      window.open(url, '_blank');
    } catch (err) {
      setError('Erro ao visualizar ficheiro');
    }
  };

  const handleDelete = async (tarefaId) => {
    if (!window.confirm('Tem certeza que deseja eliminar esta tarefa e o ficheiro?')) return;
    
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API_URL}/api/tarefas/${tarefaId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      loadTarefas();
    } catch (err) {
      setError('Erro ao eliminar tarefa');
    }
  };

  const resetForm = () => {
    setFormData({
      titulo: '',
      descricao: '',
      categoria: '',
      file: null
    });
  };

  const updateField = (field, value) => {
    setFormData({...formData, [field]: value});
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (!file.name.toLowerCase().endsWith('.pdf')) {
        setError('Apenas ficheiros PDF são permitidos');
        return;
      }
      setFormData({...formData, file});
    }
  };

  const tarefasPorCategoria = tarefas.reduce((acc, tarefa) => {
    const cat = tarefa.categoria || 'Sem Categoria';
    if (!acc[cat]) acc[cat] = [];
    acc[cat].push(tarefa);
    return acc;
  }, {});

  if (loading) return <div className="text-center py-8">Carregando...</div>;

  return (
    <div className="space-y-6">
      {/* ModuleInfo - Ajuda contextual */}
      <div className="flex items-center gap-2">
        <ModuleInfo
          descricao="Repositorio de documentos e ficheiros PDF da empresa. Permite carregar, organizar por categoria e consultar procedimentos, normas, comunicados e material de formacao. Nao confundir com a gestao de tarefas operacionais diarias (menu Tarefas Turno)."
          dados="Documentos guardados em /api/tarefas com upload de ficheiros PDF. Organizados por categorias: Procedimentos, Formacao, Normas, Comunicados, Outros."
          calculo="Sem calculos. Listagem e filtragem por categoria."
          legendas="Cada documento tem titulo, descricao, categoria e ficheiro PDF associado. Apenas administradores podem carregar ou eliminar documentos."
        />
      </div>
      {/* Header */}
      <Card className="bg-gradient-card border-primary/20">
        <CardContent className="p-4">
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div className="flex items-center gap-4">
              <FolderOpen className="h-5 w-5 text-primary" />
              <select
                value={categoriaFiltro}
                onChange={(e) => setCategoriaFiltro(e.target.value)}
                className="px-3 py-2 border rounded-md bg-background"
              >
                <option value="">Todas as Categorias</option>
                {categorias.map(cat => (
                  <option key={cat} value={cat}>{cat}</option>
                ))}
              </select>
            </div>
            
            {isAdmin && (
              <Button 
                onClick={() => { resetForm(); setShowModal(true); }} 
                className="bg-primary hover:bg-primary/90"
              >
                <Plus className="h-4 w-4 mr-2" />
                Carregar PDF
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      {error && (
        <div className="bg-destructive/10 border border-destructive text-destructive px-4 py-3 rounded">
          {error}
        </div>
      )}

      {/* Resumo */}
      <Card className="bg-gradient-card">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <FileText className="h-5 w-5" />
            Documentos Disponíveis
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="text-center">
              <p className="text-sm text-muted-foreground">Total de Documentos</p>
              <p className="text-2xl font-bold text-primary">{tarefas.length}</p>
            </div>
            <div className="text-center">
              <p className="text-sm text-muted-foreground">Categorias</p>
              <p className="text-2xl font-bold text-success">{Object.keys(tarefasPorCategoria).length}</p>
            </div>
            <div className="text-center">
              <p className="text-sm text-muted-foreground">Mais Recente</p>
              <p className="text-sm font-semibold">
                {tarefas.length > 0 
                  ? new Date(tarefas[0].created_at).toLocaleDateString('pt-PT')
                  : '-'}
              </p>
            </div>
            <div className="text-center">
              <p className="text-sm text-muted-foreground">Formato</p>
              <p className="text-2xl font-bold text-accent">PDF</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Lista de Tarefas por Categoria */}
      <div className="space-y-6">
        {Object.keys(tarefasPorCategoria).length === 0 ? (
          <Card>
            <CardContent className="p-8 text-center text-muted-foreground">
              <FileText className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p>Nenhum documento disponível</p>
              {isAdmin && (
                <Button 
                  onClick={() => setShowModal(true)} 
                  className="mt-4"
                  variant="outline"
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Carregar Primeiro Documento
                </Button>
              )}
            </CardContent>
          </Card>
        ) : (
          Object.entries(tarefasPorCategoria).map(([categoria, docs]) => (
            <Card key={categoria}>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <FolderOpen className="h-5 w-5 text-primary" />
                  {categoria}
                  <span className="text-sm text-muted-foreground font-normal">
                    ({docs.length} {docs.length === 1 ? 'documento' : 'documentos'})
                  </span>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {docs.map((tarefa) => (
                    <div
                      key={tarefa.id}
                      className="flex items-center justify-between p-4 border rounded-lg hover:bg-accent/5 transition-colors"
                    >
                      <div className="flex items-start gap-3 flex-1">
                        <div className="p-2 bg-destructive/10 rounded">
                          <FileText className="h-6 w-6 text-destructive" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <h4 className="font-semibold truncate">{tarefa.titulo}</h4>
                          {tarefa.descricao && (
                            <p className="text-sm text-muted-foreground mt-1 line-clamp-2">
                              {tarefa.descricao}
                            </p>
                          )}
                          <div className="flex items-center gap-2 mt-2 text-xs text-muted-foreground">
                            <span>{tarefa.ficheiro_nome}</span>
                            <span>•</span>
                            <span>
                              {new Date(tarefa.created_at).toLocaleDateString('pt-PT')}
                            </span>
                            <span>•</span>
                            <span>Por {tarefa.uploaded_by_nome}</span>
                          </div>
                        </div>
                      </div>
                      <div className="flex gap-2 ml-2">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleView(tarefa.id)}
                          title="Visualizar"
                        >
                          <Eye className="h-4 w-4" />
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleDownload(tarefa.id, tarefa.ficheiro_nome)}
                          title="Download"
                        >
                          <Download className="h-4 w-4" />
                        </Button>
                        {isAdmin && (
                          <Button
                            size="sm"
                            variant="outline"
                            className="text-destructive hover:text-destructive"
                            onClick={() => handleDelete(tarefa.id)}
                            title="Eliminar"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          ))
        )}
      </div>

      {/* Modal Upload */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-card rounded-lg max-w-xl w-full">
            <div className="p-6 border-b border-border">
              <h2 className="text-xl font-semibold flex items-center gap-2">
                <Upload className="h-5 w-5" />
                Carregar Documento PDF
              </h2>
            </div>
            
            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium mb-2">Título *</label>
                <input
                  type="text"
                  value={formData.titulo}
                  onChange={(e) => updateField('titulo', e.target.value)}
                  className="w-full px-3 py-2 border rounded-md bg-background"
                  placeholder="Ex: Procedimento de Abertura"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Categoria</label>
                <select
                  value={formData.categoria}
                  onChange={(e) => updateField('categoria', e.target.value)}
                  className="w-full px-3 py-2 border rounded-md bg-background"
                >
                  <option value="">Selecione uma categoria</option>
                  {categorias.map(cat => (
                    <option key={cat} value={cat}>{cat}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Descrição (opcional)</label>
                <textarea
                  value={formData.descricao}
                  onChange={(e) => updateField('descricao', e.target.value)}
                  className="w-full px-3 py-2 border rounded-md bg-background"
                  rows={3}
                  placeholder="Breve descrição do documento..."
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Ficheiro PDF *</label>
                <input
                  type="file"
                  accept=".pdf"
                  onChange={handleFileChange}
                  className="w-full px-3 py-2 border rounded-md bg-background"
                  required
                />
                {formData.file && (
                  <p className="text-sm text-success mt-2">
                    ✓ {formData.file.name}
                  </p>
                )}
              </div>

              <div className="flex justify-end gap-2 pt-4 border-t">
                <Button 
                  type="button" 
                  variant="outline" 
                  onClick={() => { setShowModal(false); resetForm(); }}
                  disabled={uploading}
                >
                  Cancelar
                </Button>
                <Button 
                  type="submit" 
                  className="bg-primary hover:bg-primary/90"
                  disabled={uploading}
                >
                  {uploading ? (
                    <>
                      <Upload className="h-4 w-4 mr-2 animate-spin" />
                      A carregar...
                    </>
                  ) : (
                    <>
                      <Upload className="h-4 w-4 mr-2" />
                      Carregar
                    </>
                  )}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default TarefasTab;
